'use client';

import { ReactNode, useState } from 'react';
import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
} from '@/primitives';
import { alpha, Theme } from '@mui/material/styles';
import MoreVert from '@mui/icons-material/MoreVert';

export interface Action {
  icon?: ReactNode;
  label: string | ReactNode;
  action: () => void;
  disabled?: boolean;
}

/** Same outer size / padding as compact kebab (registry toolbars). */
export const REGISTRY_TOOLBAR_COMPACT_CONTROL_SX = {
  width: 32,
  height: 32,
  minWidth: 32,
  padding: '4px',
  borderRadius: '5px',
  boxSizing: 'border-box' as const,
};

export const REGISTRY_TOOLBAR_COMPACT_ICON_SX = {
  fontSize: '18px',
  width: '18px',
  height: '18px',
};

/** Bordered compact icon control — same as compact DropdownActions (⋮) trigger. */
export const REGISTRY_TOOLBAR_COMPACT_OUTLINED_ICON_BUTTON_SX = {
  ...REGISTRY_TOOLBAR_COMPACT_CONTROL_SX,
  borderColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.5),
  borderWidth: 1,
  borderStyle: 'solid',
  '& svg': REGISTRY_TOOLBAR_COMPACT_ICON_SX,
  ':hover': {
    borderColor: (theme: Theme) => theme.palette.primary.main,
  },
};

/** Contained icon-only add button — matches compact kebab control. */
export const REGISTRY_TOOLBAR_COMPACT_ADD_BUTTON_SX = {
  ...REGISTRY_TOOLBAR_COMPACT_CONTROL_SX,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  '& svg': REGISTRY_TOOLBAR_COMPACT_ICON_SX,
};

export interface DropdownActionsProps {
  actions: Action[];
  /** Smaller control + menu text (registry search toolbars). */
  compact?: boolean;
  /** Tooltip on the menu trigger (default: More options). */
  tooltipTitle?: string;
}

export function DropdownActions({
  actions,
  compact,
  tooltipTitle = 'More options',
}: DropdownActionsProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: () => void) => {
    action();
    handleClose();
  };

  const trigger = (
    <IconButton
      onClick={handleClick}
      aria-label={tooltipTitle}
      sx={
        compact
          ? REGISTRY_TOOLBAR_COMPACT_OUTLINED_ICON_BUTTON_SX
          : {
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
              borderWidth: 1,
              borderStyle: 'solid',
              borderRadius: '5px',
              ':hover': {
                borderColor: (theme) => theme.palette.primary.main,
              },
            }
      }
    >
      <MoreVert color="primary" />
    </IconButton>
  );

  return (
    <>
      <Tooltip title={tooltipTitle}>{trigger}</Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {actions.map((action, index) => (
          <MenuItem
            disabled={action.disabled || false}
            key={index}
            onClick={() => handleMenuItemClick(action.action)}
            sx={
              compact
                ? {
                    fontSize: '12px',
                    minHeight: 36,
                    '& .MuiListItemIcon-root': {
                      minWidth: 32,
                      '& svg': {
                        fontSize: '18px',
                        width: '18px',
                        height: '18px',
                      },
                    },
                  }
                : undefined
            }
          >
            <ListItemIcon>{action.icon}</ListItemIcon>
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
